<template>
    <div class="CustomPropertiesManagementTable">
        <div
            v-if="items.length === 0"
            class="CustomPropertiesManagementTable__empty"
        >
            <p class="CustomPropertiesManagementTable__emptyHeading">
                No custom properties yet
            </p>
            <p class="CustomPropertiesManagementTable__emptyDescription">
                Custom properties let you add extra fields to your entities. Create your first one to get started.
            </p>
            <NewButton variant="flat" color="primary" @click="$emit('add')">
                + Add Custom Property
            </NewButton>
        </div>

        <Table v-else>
            <template #head>
                <HeadCell class="CustomPropertiesManagementTable__colDrag">
                    <EidIcon
                        :icon-src="faCircleNotch"
                        alt="saving"
                        class="CustomPropertiesManagementTable__savingSpinner animate-spin"
                        :class="{ 'CustomPropertiesManagementTable__savingSpinner--hidden': !isSaving }"
                    />
                </HeadCell>
                <HeadCell class="CustomPropertiesManagementTable__colName">
                    Property Name
                </HeadCell>
                <HeadCell class="CustomPropertiesManagementTable__colDescription">
                    Description
                </HeadCell>
                <HeadCell class="CustomPropertiesManagementTable__colType">
                    Field Type
                </HeadCell>
                <HeadCell class="CustomPropertiesManagementTable__colRequired">
                    Required
                </HeadCell>
                <HeadCell class="CustomPropertiesManagementTable__colDisplay">
                    Add to Table
                </HeadCell>
                <HeadCell class="CustomPropertiesManagementTable__colActions" />
            </template>

            <template #body>
                <!--
                    PRODUCT-26110 Task 8b Step 3: vuedraggable 2 -> 4. v4 requires an
                    `item` scoped-slot template instead of a plain default slot with a
                    caller-owned v-for, plus a mandatory `item-key` (items already carry
                    a stable unique `id`, previously used as the v-for `:key`).
                    `:model-value`/`@update:model-value` replace `v-model` -- explicit
                    binding sidesteps the MODE-2 (COMPONENT_V_MODEL not yet flipped)
                    old-style `value`/`input` v-model compilation that would otherwise
                    target props v4 no longer has.
                -->
                <draggable
                    :model-value="draggableItems"
                    tag="tbody"
                    item-key="id"
                    handle=".CustomPropertiesManagementTableRow__dragHandle"
                    ghost-class="CustomPropertiesManagementTableRow__row--ghost"
                    :disabled="isSaving"
                    @update:model-value="draggableItems = $event"
                    @end="onDragEnd"
                >
                    <template #item="{ element: item }">
                        <CustomPropertiesManagementTableRow
                            :key="item.id"
                            :item="item"
                            :displayed="isDisplayed(item.id)"
                            :display-disabled="isSaving || (!isDisplayed(item.id) && displayIds.length >= 3)"
                            :is-saving="isSaving"
                            :can-delete="canDelete"
                            @edit="$emit('edit', item)"
                            @delete="$emit('delete', item)"
                            @toggle-display="onToggleDisplay(item.id, $event)"
                        />
                    </template>
                </draggable>
            </template>
        </Table>
    </div>
</template>

<style lang="scss">
.CustomPropertiesManagementTable {
    &__savingSpinner {
        color: var(--eidQualityBlack-50);

        &--hidden {
            visibility: hidden;
        }
    }

    &__empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 64px 24px;
        text-align: center;
    }

    &__emptyHeading {
        font-size: var(--text-section-header-size);
        font-weight: var(--text-section-header-weight);
        line-height: var(--text-section-header-line-height);
        color: var(--eidSecureBlue);
        margin-bottom: 8px;
    }

    &__emptyDescription {
        font-size: var(--text-body-size);
        font-weight: var(--text-body-weight);
        line-height: var(--text-body-line-height);
        color: var(--eidTechGray);
        margin-bottom: 16px;
        max-width: 400px;
    }

    &__colDrag {
        width: 40px;
        text-align: center;
    }

    &__colName {
        width: auto;
    }

    &__colDescription {
        width: auto;
    }

    &__colType {
        width: 160px;
    }

    &__colRequired {
        width: 100px;
    }

    &__colDisplay {
        width: 140px;
    }

    &__colActions {
        width: 48px;
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import draggable from 'vuedraggable';

    import { faCircleNotch } from '@fortawesome/pro-regular-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { HeadCell, Table } from '@evidentid/dashboard-commons/components/Table';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';

    import NewButton from '@/components/new-button/NewButton.vue';

    import CustomPropertiesManagementTableRow
        from './sub-components/CustomPropertiesManagementTableRow.vue';

    export default defineComponent({
        name: 'CustomPropertiesManagementTable',
        components: {
            draggable,
            CustomPropertiesManagementTableRow,
            EidIcon,
            HeadCell,
            Table,
            NewButton,
        },

        props: {
            items: {
                type: Array as PropType<CustomProperty[]>,
                required: true,
            },
            displayIds: {
                type: Array as PropType<string[]>,
                required: true,
            },
            isSaving: {
                type: Boolean,
                default: false,
            },
            canDelete: {
                type: Boolean,
                default: false,
            },
        },
        emits: [ 'add', 'edit', 'delete', 'reorder', 'update:displayIds' ],

        data() {
            return {
                faCircleNotch,
                draggableItems: [] as CustomProperty[],
            };
        },

        watch: {
            items: {
                immediate: true,
                handler(newItems: CustomProperty[]) {
                    this.draggableItems = [ ...newItems ];
                },
            },
        },

        methods: {
            isDisplayed(id: string): boolean {
                return this.displayIds.includes(id);
            },
            onToggleDisplay(id: string, checked: boolean) {
                let newIds: string[];
                if (checked) {
                    newIds = this.draggableItems
                        .filter((item: CustomProperty) =>
                            this.displayIds.includes(item.id) || item.id === id,
                        )
                        .map((item: CustomProperty) => item.id);
                } else {
                    newIds = this.displayIds.filter((existingId: string) => existingId !== id);
                }
                this.$emit('update:displayIds', newIds);
            },
            onDragEnd() {
                const orderChanged = this.draggableItems.some(
                    (item: CustomProperty, index: number) => item.id !== this.items[index]?.id,
                );
                if (!orderChanged) {
                    return;
                }
                this.$emit('reorder', [ ...this.draggableItems ]);
            },
        },
    });
</script>
